import { keyBy } from "lodash";
import {
  OrgSelfIdQuestionsQuery,
  PublicOrgSelfIdQuestionsQuery,
  useOrgSelfIdQuestionsQuery,
  usePublicOrgSelfIdQuestionsQuery,
} from "modules/graphql.types";

// Define the SelfIdType enum
export enum SelfIdType {
  GOV_OPT_OUT_1 = "GOV_OPT_OUT_1",
  GOV_DEMOGRAPHIC_1 = "GOV_DEMOGRAPHIC_1",
  GOV_DEMOGRAPHIC_2 = "GOV_DEMOGRAPHIC_2",
  GOV_DEMOGRAPHIC_3 = "GOV_DEMOGRAPHIC_3",
  GOV_DEMOGRAPHIC_3_1 = "GOV_DEMOGRAPHIC_3_1",
  GOV_DEMOGRAPHIC_3_2 = "GOV_DEMOGRAPHIC_3_2",
  GOV_VETERAN_1 = "GOV_VETERAN_1",
  GOV_DISABILITY_1 = "GOV_DISABILITY_1",
  GOV_DISABILITY_2 = "GOV_DISABILITY_2",
  GOV_DISABILITY_3 = "GOV_DISABILITY_3",
  GOV_DISABILITY_4 = "GOV_DISABILITY_4",
  DESCRIPTION = "DESCRIPTION",
}

// Define the type of the array objects
type ArrayObject = {
  id: string;
  label?: string;
  name?: string;
  description?: string | null;
  selfIdDefaultId?: number;
  enabled?: boolean;
  selfIdType?: string;
  isGovernment?: boolean;
  questionType?: string;
  orgSelfIdQuestionOptions?:
    | { id?: string; name?: string; deletedAt?: string | null }[]
    | null;
};

// Function to transform the array objects
export function toOptions(
  arrayObject: ArrayObject,
): { label: string; value: string }[] {
  return (
    arrayObject?.orgSelfIdQuestionOptions?.map((obj) => ({
      label: obj.name ?? "",
      value: obj.id,
    })) ?? []
  );
}

// Function to group the array objects by selfIdType
function keyBySelfIdType(
  arrayObjects: ArrayObject[],
): Partial<Record<SelfIdType, ArrayObject>> {
  return keyBy(arrayObjects ?? [], (o) => {
    if (o.name === "DESCRIPTION") {
      return SelfIdType.DESCRIPTION;
    }
    return o.selfIdType;
  });
}

const useOrgGovtSelfIdQuestions = (
  orgFriendlyName: string,
  disabled?: boolean,
) => {
  const optionsResponse = useOrgSelfIdQuestionsQuery(
    {
      orgFriendlyName,
    },
    {
      enabled: !disabled && !!orgFriendlyName,
      select: (data) => {
        return keyBySelfIdType(data?.orgSelfIdQuestions ?? []);
      },
    },
  );

  return optionsResponse;
};

export const useGovtSelfIdQuestions = (
  isPublic: boolean,
  orgFriendlyName: string,
) => {
  const publicRes = usePublicSelfIdQuestions(orgFriendlyName, !isPublic);
  const orgRes = useOrgGovtSelfIdQuestions(orgFriendlyName, isPublic);

  return isPublic ? publicRes : orgRes;
};

export const useRawGovtSelfIdQuestions = <T>(
  isPublic: boolean,
  orgFriendlyName: string,
  select: (data: PublicOrgSelfIdQuestionsQuery | OrgSelfIdQuestionsQuery) => T,
) => {
  const publicRes = usePublicOrgSelfIdQuestionsQuery(
    {
      orgFriendlyName,
    },
    {
      enabled: isPublic && !!orgFriendlyName,
      select,
    },
  );

  const orgRes = useOrgSelfIdQuestionsQuery(
    {
      orgFriendlyName,
    },
    {
      enabled: !isPublic && !!orgFriendlyName,
      select,
    },
  );

  return isPublic ? publicRes : orgRes;
};

const usePublicSelfIdQuestions = (
  orgFriendlyName: string,
  disabled?: boolean,
) => {
  const optionsResponse = usePublicOrgSelfIdQuestionsQuery(
    {
      orgFriendlyName,
    },
    {
      enabled: !disabled && !!orgFriendlyName,
      select: (data) => {
        return keyBySelfIdType(data?.publicOrgSelfIdQuestions ?? []);
      },
    },
  );

  return optionsResponse;
};
